/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { readCsvHeaderFields } from '@app/app/shared/helpers/csv.helper';
import { FileUploadHelper } from '@app/app/shared/helpers/file-upload-helper';
import { SafeHtmlPipe } from '@app/app/shared/pipes/safe-html.pipe';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { PtlV3DialogShellComponent } from '@app/app/shared/styleguide/components/dialog-shell/ptl-v3-dialog-shell.component';
import { UploadFileRejection } from '@app/app/shared/styleguide/components/upload-file-area/upload-file-area.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideFileSpreadsheet, LucideX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

/** What the caller needs to run the import: the file, and the header row it declares. */
export interface V3UsersCsvUploadResult {
  file: File;
  headers: string[];
}

const ACCEPTED_FILE_TYPES = '.csv';

@Component({
  selector: 'ptl-v3-users-csv-upload-dialog',
  templateUrl: './v3-users-csv-upload-dialog.component.html',
  styleUrls: ['./v3-users-csv-upload-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, StyleguideModule, PtlV3DialogShellComponent, SafeHtmlPipe],
})
export class V3UsersCsvUploadDialogComponent {
  protected readonly LucideX = LucideX;
  protected readonly LucideFileSpreadsheet = LucideFileSpreadsheet;
  protected readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;

  protected readonly csvFile = signal<File | null>(null);
  protected readonly csvErrorKey = signal<string | null>(null);

  private readonly dialogRef = inject(MatDialogRef<V3UsersCsvUploadDialogComponent, V3UsersCsvUploadResult>);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);
  private readonly transloco = inject(TranslocoService);

  private csvHeader: Promise<string[]> = Promise.resolve([]);

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected onFileSelected(file: File): void {
    this.csvErrorKey.set(null);
    this.csvFile.set(file);
    this.csvHeader = readCsvHeaderFields(file);
  }

  protected onFilesRejected(reason: UploadFileRejection): void {
    if (reason === 'WRONG_TYPE') {
      this.csvErrorKey.set('translations.membersNew.controlPanel.usersUpload.error.invalidFileType');
      return;
    }
    this.toastService.showFail(this.transloco.translate('translations.uploadFileCountLimitError'));
  }

  protected removeSelectedFile(): void {
    this.csvErrorKey.set(null);
    this.csvHeader = Promise.resolve([]);
    this.csvFile.set(null);
  }

  protected async uploadUsers(): Promise<void> {
    const file = this.csvFile();

    if (!file) {
      this.csvErrorKey.set('translations.membersNew.controlPanel.usersUpload.error.fileRequired');
      return;
    }

    if (!FileUploadHelper.isValidCsvFile(file)) {
      this.csvErrorKey.set('translations.membersNew.controlPanel.usersUpload.error.invalidFileType');
      return;
    }

    this.csvErrorKey.set(null);
    this.dialogRef.close({ file, headers: await this.csvHeader });
  }
}
